import React, { FC, useState } from 'react';
import { Order, Rating, RatingReasons } from 'models/order';
import { Box, Button } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { rateOrderTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import RateOrderContent from 'components/orders/order-details/rate-order/rate-order-content.component';
import { useSeedOnOpen } from 'hooks/useSeedOnOpen';

type RateOrderDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order: Order;
};

const RateOrderDialog: FC<RateOrderDialogProps> = ({ onUpdate, onClose, open, order }: RateOrderDialogProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [rating, setRating] = useState<Rating>(order.rating);
    useSeedOnOpen(open, () => setRating(order.rating));
    const { showBoundary } = useDenthubErrorBoundary();

    const handleRating = (rating: Rating) => {
        setRating(rating);
    };

    const [rateOrder] = useMutation(rateOrderTemplate, {
        onCompleted: ({ rateOrder }) => {
            onClose();
            onUpdate?.(rateOrder);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleRateOrder = async () => {
        const ratingObj = {
            satisfied: rating.satisfied,
            notSatisfied: rating.notSatisfied,
            reasons: rating.reasons,
            comments: rating.comments,
        };
        await rateOrder({
            variables: {
                orderId: order?._id,
                rating: ratingObj,
            },
        });
    };

    const disableRateButton = () => {
        if (rating.satisfied) {
            return false;
        } else if (rating.notSatisfied) {
            return rating.reasons?.every((reason: RatingReasons) => !reason.value);
        }
        return true;
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            title={translate('orders:RATE_THE_ORDER')}
            actions={
                <Button
                    disabled={disableRateButton()}
                    sx={{ minWidth: 250 }}
                    variant="contained"
                    onClick={handleRateOrder}
                    data-test="rate-order-dialog.rate-order-button"
                >
                    {translate('orders:RATE_THE_ORDER')}
                </Button>
            }
        >
            <Box mb={3}>
                <RateOrderContent onHandleRating={handleRating} order={order} />
            </Box>
        </DHDialog>
    );
};
export default RateOrderDialog;
